/**
 * Checkpoint helpers (S3.2) — shared by the detail sections and the
 * VendorConfirmDrawer.
 *
 * The A1/A2/A3 checkpoints ALWAYS render as the sanctioned bilingual labels
 * from `ofcVocabulary.ts` (e.g. 「A1 估計」) — bare A-codes live in
 * code/tests only (holistic plan, ux-lens finding 7).
 */

import { useTranslation } from 'react-i18next';
import {
  VENDOR_CHECKPOINTS,
  formatVendorCheckpoint,
} from '@/constants/ofcVocabulary';
import type { VendorCheckpointCode } from '@/constants/ofcVocabulary';
import type { OccAllocation, OccLine } from '@/types/api/occ';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import type { VendorCheckpointRecord } from '@/stores/poStore';

export const CHECKPOINT_CODES = Object.keys(VENDOR_CHECKPOINTS) as VendorCheckpointCode[];

/** OCC allocation bucket field per checkpoint. */
export const CHECKPOINT_BUCKET: Record<
  VendorCheckpointCode,
  'qtyEstimated' | 'qtyNoticeConfirmed' | 'qtyLoaded'
> = {
  A1: 'qtyEstimated',
  A2: 'qtyNoticeConfirmed',
  A3: 'qtyLoaded',
};

/** Localized sanctioned label, e.g. "A1 估計" / "A1 Estimate". */
export function useCheckpointLabel() {
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('zh') ? 'zh' : 'en';
  return (code: VendorCheckpointCode) => formatVendorCheckpoint(code, lang);
}

/** The OCC allocation row carrying a PO line's buckets, if projected. */
export function occAllocationForPoLine(
  occLines: OccLine[],
  poLineId: string
): OccAllocation | undefined {
  return occLines
    .flatMap((line) => line.allocations)
    .find((alloc) => alloc.poLineId === poLineId);
}

export interface CheckpointStatus {
  code: VendorCheckpointCode;
  /** True once any bucket qty landed or a capture record exists. */
  recorded: boolean;
  /** Latest capture record for this checkpoint (client-side log). */
  record: VendorCheckpointRecord | null;
  /** ISO timestamp of the recording, when known. */
  at: string | null;
}

/** Derive the three-checkpoint timeline for one PO. */
export function deriveCheckpointStatuses(
  po: PurchaseOrder,
  occLines: OccLine[],
  records: VendorCheckpointRecord[]
): CheckpointStatus[] {
  const allocations = occLines
    .flatMap((line) => line.allocations)
    .filter((alloc) => alloc.poId === po.id);

  return CHECKPOINT_CODES.map((code) => {
    const record = records.find((row) => row.checkpoint === code) ?? null;
    const bucketField = CHECKPOINT_BUCKET[code];
    const bucketRecorded = allocations.some((alloc) => alloc[bucketField] > 0);
    const a1Acknowledged = code === 'A1' && po.vendorAcknowledgedAt != null;
    return {
      code,
      recorded: Boolean(record) || bucketRecorded || a1Acknowledged,
      record,
      at: record?.at ?? (a1Acknowledged ? po.vendorAcknowledgedAt : null),
    };
  });
}

/** First unrecorded checkpoint (drawer default); A3 when all recorded. */
export function nextCheckpoint(statuses: CheckpointStatus[]): VendorCheckpointCode {
  return statuses.find((status) => !status.recorded)?.code ?? 'A3';
}
